import {
  Badge,
  Box,
  Flex,
  Grid,
  GridItem,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  VStack
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { CopyButton } from '../../../components/ActionButton'
import { FormField } from '../../../components/FormField'
import { PageTitle } from '../../../components/PageTitle'
import { SectionCard } from '../../../components/SectionCard'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import { CoursesService } from '../../../services/CoursesService/CoursesService'
import { IKnowledgeArea, ISelectCourse } from '../../../services/CoursesService/interfaces'

interface ISnippetConfig {
  courseFormatMode: 'preset' | 'user_choice'
  courseFormatPreset: string
  knowledgeAreaMode: 'preset' | 'user_choice'
  knowledgeAreaPreset: string
  courseMode: 'preset' | 'user_choice' | 'all'
  coursePreset: ISelectCourse | null
  courseOptions: ISelectCourse[]
  apiBaseUrl: string
  thankYouUrl: string
  formTitle: string
}

// Passport is offered only as a preset format: its form has no area or course to pick
const PASSPORT_COURSE_FORMAT = 'Online Gravado (Passaporte)'
// salva.php drops a lead with no course and only routes "Online" to the recorded consultants
const PASSPORT_SUBMITTED_COURSE = 'Passaporte'
const PASSPORT_SUBMITTED_FORMAT = 'Online'
// salva.php applies each tag by name (creating it if missing); this one triggers the passport automation in AC
const PASSPORT_AC_TAG = 'formulario-monte-seu-kit'

const COURSE_FORMAT_OPTIONS = [
  { value: 'Presencial', label: 'Presencial' },
  { value: 'Online Ao Vivo', label: 'Online Ao Vivo' },
  { value: 'Online', label: 'Online Gravado' }
]

const PRESET_FORMAT_OPTIONS = [
  ...COURSE_FORMAT_OPTIONS,
  { value: PASSPORT_COURSE_FORMAT, label: PASSPORT_COURSE_FORMAT }
]

// Random per generation: it pairs a JS with the HTML generated alongside it
function createSnippetId(): string {
  return Math.random().toString(36).slice(2, 10)
}

function generateSnippet(config: ISnippetConfig, snippetId: string): { html: string; js: string } {
  const {
    courseFormatMode,
    courseFormatPreset,
    knowledgeAreaMode,
    knowledgeAreaPreset,
    courseMode,
    coursePreset,
    courseOptions,
    apiBaseUrl,
    thankYouUrl,
    formTitle
  } = config

  // A preset passport form only carries hidden fields with what salva.php needs
  const isPassportPreset = courseFormatMode === 'preset' && courseFormatPreset === PASSPORT_COURSE_FORMAT
  const needsApiFetch = !isPassportPreset && knowledgeAreaMode === 'user_choice'
  const needsCourseApiFetch = !isPassportPreset && courseMode === 'all'
  // Determines which course boolean field to filter by; null when format is left to user
  const formatField: string | null =
    courseFormatMode === 'preset'
      ? courseFormatPreset === 'Presencial'
        ? 'in_person'
        : courseFormatPreset === 'Online Ao Vivo'
          ? 'live_online'
          : courseFormatPreset === 'Online'
            ? 'recorded'
            : null
      : null

  const courseFormatHtml =
    courseFormatMode === 'preset'
      ? `\n    <input type="hidden" name="formato_curso" value="${isPassportPreset ? PASSPORT_SUBMITTED_FORMAT : courseFormatPreset}">`
      : `\n    <div class="abra-form__group">
      <select class="abra-form__select" name="formato_curso" required>
        <option value="" disabled selected>* Formato do Curso</option>
        ${COURSE_FORMAT_OPTIONS.map((option) => `<option value="${option.value}">${option.label}</option>`).join('\n')}
      </select>
      <span class="abra-form__error-msg abra-formato-err">Selecione o formato do curso.</span>
    </div>`

  // When format is preset and courses are statically defined, derive areas from courseOptions at generation time
  const staticAreasFromCourseOptions =
    knowledgeAreaMode === 'user_choice' && courseMode === 'user_choice' && formatField !== null
      ? Array.from(
          new Map(courseOptions.flatMap((c) => c.knowledge_areas || []).map((ka) => [ka.id, ka])).values()
        ).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      : null

  const knowledgeAreaHtml = isPassportPreset
    ? ''
    : knowledgeAreaMode === 'preset'
      ? `\n    <input type="hidden" name="area" value="${knowledgeAreaPreset}">`
      : staticAreasFromCourseOptions !== null
        ? `\n    <div class="abra-form__group">
      <select class="abra-form__select" name="area" required>
        <option value="" disabled selected>* Área do Curso</option>
        ${staticAreasFromCourseOptions.map((a) => `<option value="${a.name}">${a.name}</option>`).join('\n        ')}
      </select>
      <span class="abra-form__error-msg abra-area-err">Selecione a área do curso.</span>
    </div>`
        : `\n    <div class="abra-form__group">
      <select class="abra-form__select" name="area" required>
        <option value="" disabled selected>* Área do Curso</option>
      </select>
      <span class="abra-form__error-msg abra-area-err">Selecione a área do curso.</span>
    </div>`

  type IListsMap = Record<string, { in_person: string[]; live_online: string[]; recorded: string[] }>
  const listsMap: IListsMap = isPassportPreset
    ? {}
    : courseMode === 'preset' && coursePreset
      ? {
          [coursePreset.name]: {
            in_person: coursePreset.active_campaign_in_person_lists ?? [],
            live_online: coursePreset.active_campaign_live_online_lists ?? [],
            recorded: coursePreset.active_campaign_recorded_lists ?? []
          }
        }
      : courseMode === 'all'
        ? {}
        : courseOptions.reduce<IListsMap>((acc, o) => {
            acc[o.name] = {
              in_person: o.active_campaign_in_person_lists ?? [],
              live_online: o.active_campaign_live_online_lists ?? [],
              recorded: o.active_campaign_recorded_lists ?? []
            }
            return acc
          }, {})

  const courseHtml = isPassportPreset
    ? `\n    <input type="hidden" name="curso" value="${PASSPORT_SUBMITTED_COURSE}">`
    : courseMode === 'preset'
      ? `\n    <input type="hidden" name="curso" value="${coursePreset?.name ?? ''}">`
      : courseMode === 'all'
        ? `\n    <div class="abra-form__group">
      <select class="abra-form__select" name="curso" required>
        <option value="" disabled selected>* Curso</option>
      </select>
      <span class="abra-form__error-msg abra-curso-err">Selecione o curso.</span>
    </div>`
        : courseOptions.length > 0
          ? `\n    <div class="abra-form__group">
      <select class="abra-form__select" name="curso" required>
        <option value="" disabled selected>* Curso</option>
        ${[...courseOptions]
          .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
          .map((o) => {
            const areas = JSON.stringify(o.knowledge_areas?.map((ka) => ka.name) ?? []).replace(/"/g, '&quot;')
            const inPerson = o.in_person ? '1' : '0'
            const liveOnline = o.live_online ? '1' : '0'
            const recorded = o.recorded ? '1' : '0'
            return `<option value="${o.name}" data-areas="${areas}" data-in-person="${inPerson}" data-live-online="${liveOnline}" data-recorded="${recorded}">${o.name}</option>`
          })
          .join('\n        ')}
      </select>
      <span class="abra-form__error-msg abra-curso-err">Selecione o curso.</span>
    </div>`
          : ''

  const listsMapScript =
    courseMode === 'all' ? 'var abraListsMap = {}' : `var abraListsMap = ${JSON.stringify(listsMap)};`
  const submittedTags = isPassportPreset ? JSON.stringify([PASSPORT_AC_TAG]) : ''

  // When format is preset, skip fetching areas separately — they are derived from course data with the format filter applied
  const needsAreaApiFetch = needsApiFetch && formatField === null

  // Shared fetch helpers — each runs only once per page regardless of how many forms are present
  const sharedAreaFetchScript = needsAreaApiFetch
    ? `
  var abraAreaFetchPromise = null
  function abraFetchAreas() {
    if (!abraAreaFetchPromise) {
      abraAreaFetchPromise = fetch('${apiBaseUrl.replace(/\/$/, '')}/api/website/courses/knowledge-areas')
        .then(function(res) { return res.json() })
        .then(function(data) { return data.data })
        .catch(function() { return null })
    }
    return abraAreaFetchPromise
  }`
    : ''

  const courseFetchParams =
    courseFormatMode === 'preset' && formatField !== null ? `${formatField}=1` : 'in_person=1&live_online=1&recorded=1'

  const sharedCourseFetchScript = needsCourseApiFetch
    ? `
  var abraCourseFetchPromise = null
  function abraFetchCourses() {
    if (!abraCourseFetchPromise) {
      abraCourseFetchPromise = fetch('${apiBaseUrl.replace(/\/$/, '')}/api/website/courses/select?${courseFetchParams}')
        .then(function(res) { return res.json() })
        .catch(function() { return null })
    }
    return abraCourseFetchPromise
  }`
    : ''

  // Per-form dynamic area population (when areas come from a dedicated API call)
  const perFormAreaFetchInit = needsAreaApiFetch
    ? `
    var areaSelect = wrapper.querySelector('[name="area"]')
    if (areaSelect) {
      abraFetchAreas().then(function(items) {
        if (!items) {
          areaSelect.innerHTML = '<option value="" disabled selected>Erro ao carregar</option>'
          return
        }
        areaSelect.innerHTML = '<option value="" disabled selected>* Área do Curso</option>'
        items.forEach(function(item) {
          areaSelect.innerHTML += '<option value="' + item.label + '">' + item.label + '</option>'
        })
      })
    }`
    : ''

  // Per-form dynamic course population (when courseMode === 'all')
  const perFormCourseFetchInit = needsCourseApiFetch
    ? knowledgeAreaMode === 'preset' && knowledgeAreaPreset
      ? `
    // Carrega cursos da API (filtrados por área: ${knowledgeAreaPreset}${
      formatField !== null ? `, formato: ${courseFormatPreset}` : ''
    })
    var cursoSelect = wrapper.querySelector('[name="curso"]')
    if (cursoSelect) {
      abraFetchCourses().then(function(data) {
        if (!data) {
          cursoSelect.innerHTML = '<option value="" disabled selected>Erro ao carregar</option>'
          return
        }
        var abraAreaCourses = data.filter(function(item) {
          return item.knowledge_areas && item.knowledge_areas.some(function(ka) { return ka.name === '${knowledgeAreaPreset}' })
        })
        abraAreaCourses.forEach(function(item) {
          abraListsMap[item.name] = {
            in_person: item.active_campaign_in_person_lists || [],
            live_online: item.active_campaign_live_online_lists || [],
            recorded: item.active_campaign_recorded_lists || []
          }
        })${
          formatField !== null
            ? `
        cursoSelect.innerHTML = '<option value="" disabled selected>* Curso</option>'
        abraAreaCourses
          .filter(function(item) { return !!item.${formatField} })
          .forEach(function(item) {
            cursoSelect.innerHTML += '<option value="' + item.name + '">' + item.name + '</option>'
          })`
            : `
        function abraPopulateCursosByFormat(formatValue) {
          var filtered = abraAreaCourses
          if (formatValue === 'Presencial') {
            filtered = filtered.filter(function(item) { return !!item.in_person })
          } else if (formatValue === 'Online Ao Vivo') {
            filtered = filtered.filter(function(item) { return !!item.live_online })
          } else if (formatValue === 'Online') {
            filtered = filtered.filter(function(item) { return !!item.recorded })
          }
          cursoSelect.innerHTML = '<option value="" disabled selected>* Curso</option>'
          filtered.forEach(function(item) {
            cursoSelect.innerHTML += '<option value="' + item.name + '">' + item.name + '</option>'
          })
        }
        var formatEl = wrapper.querySelector('[name="formato_curso"]')
        abraPopulateCursosByFormat(formatEl ? formatEl.value : '')
        if (formatEl) {
          formatEl.addEventListener('change', function() {
            abraPopulateCursosByFormat(formatEl.value)
          })
        }`
        }
      })
    }`
      : `
    // Carrega todos os cursos da API
    var cursoSelect = wrapper.querySelector('[name="curso"]')
    if (cursoSelect) {
      abraFetchCourses().then(function(abraAllCourses) {
        if (!abraAllCourses) {
          cursoSelect.innerHTML = '<option value="" disabled selected>Erro ao carregar</option>'
          return
        }
        abraAllCourses.forEach(function(item) {
          abraListsMap[item.name] = {
            in_person: item.active_campaign_in_person_lists || [],
            live_online: item.active_campaign_live_online_lists || [],
            recorded: item.active_campaign_recorded_lists || []
          }
        })${
          knowledgeAreaMode === 'user_choice' && formatField !== null
            ? `
        // Derive area options from courses filtered by preset format
        var abraAreaSelect = wrapper.querySelector('[name="area"]')
        if (abraAreaSelect) {
          var uniqueAreas = {}
          abraAllCourses
            .filter(function(item) { return !!item.${formatField} })
            .forEach(function(item) {
              if (item.knowledge_areas) {
                item.knowledge_areas.forEach(function(ka) { uniqueAreas[ka.name] = true })
              }
            })
          var sortedAreas = Object.keys(uniqueAreas).sort(function(a, b) { return a.localeCompare(b, 'pt-BR') })
          abraAreaSelect.innerHTML = '<option value="" disabled selected>* Área do Curso</option>'
          sortedAreas.forEach(function(name) {
            abraAreaSelect.innerHTML += '<option value="' + name + '">' + name + '</option>'
          })
        }`
            : ''
        }
        function abraPopulateCursos(areaName, formatValue) {
          var filtered = abraAllCourses
          if (areaName) {
            filtered = filtered.filter(function(item) {
              return item.knowledge_areas && item.knowledge_areas.some(function(ka) { return ka.name === areaName })
            })
          }
          if (formatValue === 'Presencial') {
            filtered = filtered.filter(function(item) { return !!item.in_person })
          } else if (formatValue === 'Online Ao Vivo') {
            filtered = filtered.filter(function(item) { return !!item.live_online })
          } else if (formatValue === 'Online') {
            filtered = filtered.filter(function(item) { return !!item.recorded })
          }
          cursoSelect.innerHTML = '<option value="" disabled selected>* Curso</option>'
          filtered.forEach(function(item) {
            cursoSelect.innerHTML += '<option value="' + item.name + '">' + item.name + '</option>'
          })
        }
        var areaEl = wrapper.querySelector('[name="area"]')
        abraPopulateCursos(areaEl ? areaEl.value : '', '${courseFormatMode === 'preset' ? courseFormatPreset : ''}')
        if (areaEl) {
          areaEl.addEventListener('change', function() {${
            courseFormatMode === 'user_choice'
              ? `
            var formatEl = wrapper.querySelector('[name="formato_curso"]')
            abraPopulateCursos(areaEl.value, formatEl ? formatEl.value : '')`
              : `
            abraPopulateCursos(areaEl.value, '${courseFormatPreset}')`
          }
          })
        }${
          courseFormatMode === 'user_choice'
            ? `
        var formatEl = wrapper.querySelector('[name="formato_curso"]')
        if (formatEl) {
          formatEl.addEventListener('change', function() {
            var areaEl2 = wrapper.querySelector('[name="area"]')
            abraPopulateCursos(areaEl2 ? areaEl2.value : '', formatEl.value)
          })
        }`
            : ''
        }
      })
    }`
    : ''

  // Per-form static course filtering by area and/or format (courseMode === 'user_choice')
  const perFormStaticFilterInit =
    !isPassportPreset &&
    courseMode === 'user_choice' &&
    (knowledgeAreaMode === 'user_choice' || courseFormatMode === 'user_choice')
      ? `
    var abraCursoSelect = wrapper.querySelector('[name="curso"]')
    var abraAreaSelect = wrapper.querySelector('[name="area"]')
    var abraFormatoSelect = wrapper.querySelector('[name="formato_curso"]')
    function abraFilterCursos() {
      if (!abraCursoSelect) return
      var selectedArea = abraAreaSelect ? abraAreaSelect.value : ''
      var selectedFormat = abraFormatoSelect ? abraFormatoSelect.value : ''
      Array.prototype.forEach.call(abraCursoSelect.options, function(opt) {
        if (!opt.value) return
        var areas = JSON.parse(opt.getAttribute('data-areas') || '[]')
        var matchesArea = !selectedArea || areas.indexOf(selectedArea) !== -1
        var matchesFormat =
          !selectedFormat ||
          (selectedFormat === 'Presencial' && opt.getAttribute('data-in-person') === '1') ||
          (selectedFormat === 'Online Ao Vivo' && opt.getAttribute('data-live-online') === '1') ||
          (selectedFormat === 'Online' && opt.getAttribute('data-recorded') === '1')
        opt.style.display = matchesArea && matchesFormat ? '' : 'none'
      })
      abraCursoSelect.value = ''
    }
    if (abraAreaSelect) { abraAreaSelect.addEventListener('change', abraFilterCursos) }
    if (abraFormatoSelect) { abraFormatoSelect.addEventListener('change', abraFilterCursos) }`
      : ''

  // When the user picks a format, reload the area select using the format-specific endpoint
  const perFormAreaByFormatInit =
    courseFormatMode === 'user_choice' && knowledgeAreaMode === 'user_choice'
      ? `
    var areaByFormatEl = wrapper.querySelector('[name="area"]')
    var formatTriggerEl = wrapper.querySelector('[name="formato_curso"]')
    var abraFormatTypeMap = { 'Presencial': 'cursos-presenciais', 'Online Ao Vivo': 'cursos-ead-ao-vivo', 'Online': 'online-gravado' }
    var abraAreasByFormatCache = {}
    function abraLoadAreasByFormat(formatType) {
      if (abraAreasByFormatCache[formatType]) {
        return Promise.resolve(abraAreasByFormatCache[formatType])
      }
      return fetch('${apiBaseUrl.replace(/\/$/, '')}/api/website/courses/knowledge-areas-by-format?type=' + formatType)
        .then(function(res) { return res.json() })
        .then(function(data) {
          var areas = (data && data.data) ? data.data : []
          abraAreasByFormatCache[formatType] = areas
          return areas
        })
        .catch(function() { return null })
    }
    function abraPopulateAreaSelect(areas) {
      areaByFormatEl.value = ''
      areaByFormatEl.innerHTML = '<option value="" disabled selected>* Área do Curso</option>'
      if (!areas) {
        areaByFormatEl.innerHTML = '<option value="" disabled selected>Erro ao carregar</option>'
        return
      }
      areas.forEach(function(name) {
        areaByFormatEl.innerHTML += '<option value="' + name + '">' + name + '</option>'
      })
    }
    if (formatTriggerEl && areaByFormatEl) {
      formatTriggerEl.addEventListener('change', function() {
        var formatType = abraFormatTypeMap[formatTriggerEl.value]
        areaByFormatEl.value = ''
        areaByFormatEl.innerHTML = '<option value="" disabled selected>* Área do Curso</option>'
        if (!formatType) return
        abraLoadAreasByFormat(formatType).then(abraPopulateAreaSelect)
      })
    }`
      : ''

  const htmlSnippet = `<style>
    .abra-form-wrapper {
      font-family: 'Segoe UI', Arial, sans-serif;
      max-width: 400px;
      margin: 0 auto;
      padding: 28px 24px;
      background: #ffffff;
      border-radius: 8px;
      box-shadow: 0 2px 16px rgba(0,0,0,0.10);
    }

    .abra-form__group {
      margin-bottom: 12px;
      position: relative;
    }

    .abra-form__input {
      width: 100%;
      padding: 13px 14px;
      border: 1.5px solid #c8c8c8;
      border-radius: 4px;
      font-size: 14px;
      color: #1a1a1a;
      background: #fff;
      transition: border-color 0.2s;
      box-sizing: border-box;
      outline: none;
      appearance: none;
      -webkit-appearance: none;
    }

    .abra-form__input::placeholder {
      color: #888;
    }

    .abra-form__input:hover {
      border: 1.5px solid #c8c8c8;
    }

    .abra-form__input:focus {
      border: 1.5px solid #555;
    }

    .abra-form__input.abra-error {
      border: 1.5px solid #e53e3e;
    }

    .abra-form__error-msg {
      display: none;
      font-size: 11px;
      color: #e53e3e;
      margin-top: 3px;
    }

    .abra-form__error-msg.abra-visible {
      display: block;
    }

    .abra-phone-wrapper {
      display: flex;
      align-items: stretch;
      border: 1.5px solid #c8c8c8;
      border-radius: 4px;
      overflow: hidden;
      background: #fff;
      transition: border-color 0.2s;
    }

    .abra-phone-wrapper:focus-within {
      border: 1.5px solid #555;
    }

    .abra-phone-wrapper.abra-error {
      border: 1.5px solid #e53e3e;
    }

    .abra-phone-flag {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 0 10px;
      background: #fff;
      border-right: 1.5px solid #c8c8c8;
      font-size: 14px;
      color: #444;
      white-space: nowrap;
      cursor: default;
      user-select: none;
    }

    .abra-phone-flag span {
      font-size: 18px;
      line-height: 1;
    }

    .abra-phone-input {
      flex: 1;
      padding: 13px 14px;
      border: none;
      outline: none;
      font-size: 14px;
      color: #1a1a1a;
      background: transparent;
      min-width: 0;
    }

    .abra-phone-input::placeholder {
      color: #888;
    }

    .abra-form__select {
      width: 100%;
      padding: 13px 14px;
      border: 1.5px solid #c8c8c8;
      border-radius: 4px;
      font-size: 14px;
      color: #1a1a1a;
      background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23555' d='M6 8L0 0h12z'/%3E%3C/svg%3E") no-repeat right 14px center;
      background-size: 10px;
      box-sizing: border-box;
      outline: none;
      appearance: none;
      -webkit-appearance: none;
      cursor: pointer;
      transition: border-color 0.2s;
    }

    .abra-form__select:focus {
      border-color: #555;
    }

    .abra-form__select.abra-error {
      border-color: #e53e3e;
    }

    .abra-form__submit {
      width: 100%;
      padding: 14px;
      background: #111;
      color: #fff;
      border: none;
      border-radius: 4px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      margin-top: 4px;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      transition: background 0.2s, transform 0.1s;
    }

    .abra-form__submit:hover {
      background: #333;
    }

    .abra-form__submit:active {
      transform: scale(0.99);
    }

    .abra-form__submit:disabled {
      background: #aaa;
      cursor: not-allowed;
    }

  </style>

  <div class="abra-form-wrapper" data-abra-snippet="${snippetId}">${
    formTitle
      ? `
    <p style="text-align:center;font-size:17px;font-weight:700;margin:0 0 18px;color:#1a1a1a;">${formTitle}</p>`
      : ''
  }
    <form class="abra-contact-form" novalidate>
      <div class="abra-form__group">
        <input class="abra-form__input" type="text" name="nome" placeholder="* Nome" required>
        <span class="abra-form__error-msg abra-nome-err">Informe seu nome completo.</span>
      </div>
      <div class="abra-form__group">
        <input class="abra-form__input" type="email" name="email" placeholder="* E-mail" required>
        <span class="abra-form__error-msg abra-email-err">Informe um e-mail válido.</span>
      </div>
      <div class="abra-form__group">
        <div class="abra-phone-wrapper">
          <div class="abra-phone-flag"><span>🇧🇷</span> +55</div>
          <input class="abra-phone-input" type="tel" name="telefone" placeholder="* Telefone" maxlength="15" required>
        </div>
        <span class="abra-form__error-msg abra-telefone-err">Informe um telefone válido.</span>
      </div>${courseFormatHtml}${knowledgeAreaHtml}${courseHtml}
      <button class="abra-form__submit" type="submit">Fale com um especialista</button>
    </form>
  </div>`

  const jsSnippet = `<script>
    (function() {
      ${listsMapScript}
      var abraThankYouUrl = ${JSON.stringify(thankYouUrl)};
      ${sharedAreaFetchScript}
      ${sharedCourseFetchScript}
      function validateEmail(email) {
        if (!email || typeof email !== "string") return false;

        if (email.includes(" ")) return false;

        const parts = email.split("@");
        if (parts.length !== 2) return false;

        const local = parts[0];
        const domain = parts[1];

        if (!local || !domain) return false;
        if (!domain.includes(".")) return false;

        const allowed = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789._%+-";

        for (let i = 0; i < local.length; i++) {
          if (!allowed.includes(local[i])) return false;
        }

        const domainAllowed = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.-";

        for (let i = 0; i < domain.length; i++) {
          if (!domainAllowed.includes(domain[i])) return false;
        }

        if (local.startsWith(".") || local.endsWith(".")) return false;
        if (domain.startsWith(".") || domain.endsWith(".")) return false;
        if (email.includes("..")) return false;

        const domainParts = domain.split(".");
        if (domainParts.some(p => p.length === 0)) return false;

        const tld = domainParts[domainParts.length - 1];
        if (tld.length < 2) return false;

        return true;
      }

      function validatePhone(phone) {
        return phone.replace(/[^0-9]/g, '').length >= 10
      }

      function setError(inputEl, errEl, show) {
        if (show) {
          inputEl.classList.add('abra-error')
          errEl.classList.add('abra-visible')
        } else {
          inputEl.classList.remove('abra-error')
          errEl.classList.remove('abra-visible')
        }
        return !show
      }

      function abraInitForm(wrapper) {
        // Phone mask
        var telefoneInput = wrapper.querySelector('[name="telefone"]')
        telefoneInput.addEventListener('input', function(e) {
          let v = e.target.value.replace(/[^0-9]/g, '').slice(0, 11)
          if (v.length <= 10) {
            v = v.replace(/([0-9]{2})([0-9]{4})([0-9]{0,4})/, '($1) $2-$3')
          } else {
            v = v.replace(/([0-9]{2})([0-9]{5})([0-9]{0,4})/, '($1) $2-$3')
          }
          e.target.value = v.replace(/-$/, '')
        })
        ${perFormAreaFetchInit}
        ${perFormAreaByFormatInit}
        ${perFormCourseFetchInit}
        ${perFormStaticFilterInit}

        var form = wrapper.querySelector('.abra-contact-form')
        form.addEventListener('submit', function(e) {
          e.preventDefault()
          var nomeInput = wrapper.querySelector('[name="nome"]')
          var emailInput = wrapper.querySelector('[name="email"]')
          var phoneInput = wrapper.querySelector('[name="telefone"]')
          var nome = nomeInput.value.trim()
          var email = emailInput.value.trim()
          var telefone = phoneInput.value.trim()

          var valid = true
          valid = setError(nomeInput, wrapper.querySelector('.abra-nome-err'), nome.length < 2) && valid
          valid = setError(emailInput, wrapper.querySelector('.abra-email-err'), !validateEmail(email)) && valid
          valid = setError(wrapper.querySelector('.abra-phone-wrapper'), wrapper.querySelector('.abra-telefone-err'), !validatePhone(telefone)) && valid

          var formatSelect = wrapper.querySelector('select[name="formato_curso"]')
          if (formatSelect) {
            valid = setError(formatSelect, wrapper.querySelector('.abra-formato-err'), !formatSelect.value) && valid
          }
          var areaSelect = wrapper.querySelector('select[name="area"]')
          if (areaSelect) {
            valid = setError(areaSelect, wrapper.querySelector('.abra-area-err'), !areaSelect.value) && valid
          }
          var courseSelect = wrapper.querySelector('select[name="curso"]')
          if (courseSelect) {
            valid = setError(courseSelect, wrapper.querySelector('.abra-curso-err'), !courseSelect.value) && valid
          }

          if (!valid) return

          var btn = wrapper.querySelector('.abra-form__submit')
          btn.disabled = true
          btn.textContent = 'Enviando...'

          // Getting UTM params
          var urlParams = new URLSearchParams(window.location.search)
          var utmParams = {
            utm_campaign: urlParams.get('utm_campaign') || '',
            utm_source: urlParams.get('utm_source') || '',
            utm_medium: urlParams.get('utm_medium') || '',
            utm_content: urlParams.get('utm_content') || '',
            utm_term: urlParams.get('utm_term') || ''
          }

          var formatEl = wrapper.querySelector('[name="formato_curso"]')
          var finalFormat = formatEl ? formatEl.value : ''

          var areaEl = wrapper.querySelector('[name="area"]')
          var areaValue = areaEl ? areaEl.value : ''

          var courseEl = wrapper.querySelector('[name="curso"]')
          var courseName = courseEl ? courseEl.value : ''

          var phoneRaw = telefone.replace(/[^0-9]/g, '')

          var submitFormData = new FormData()
          submitFormData.append('source', 'LP Great Pages')
          submitFormData.append('nome', nome)
          submitFormData.append('email', email)
          submitFormData.append('telefone', phoneRaw)
          submitFormData.append('telefone-ddi', '55')
          submitFormData.append('is-compre-aqui', '0')
          submitFormData.append('utm_campaign', utmParams.utm_campaign)
          submitFormData.append('utm_source', utmParams.utm_source)
          submitFormData.append('utm_medium', utmParams.utm_medium)
          submitFormData.append('utm_content', utmParams.utm_content)
          submitFormData.append('utm_term', utmParams.utm_term)
          submitFormData.append('formato', finalFormat)
          submitFormData.append('area-online', areaValue)
          submitFormData.append('curso', courseName)
          submitFormData.append('tags', ${JSON.stringify(submittedTags)})
          var courseListData = abraListsMap[courseName] || { in_person: [], live_online: [], recorded: [] }
          var listArray = finalFormat === 'Presencial' ? courseListData.in_person : finalFormat === 'Online' ? courseListData.recorded : courseListData.live_online
          submitFormData.append('lists', JSON.stringify(listArray))

          fetch('https://cursos.abra.com.br/salva.php', {
            method: 'POST',
            body: submitFormData
          })
            .then(function(res) {
              if (!res.ok) throw new Error()
              btn.disabled = false
              btn.textContent = 'Fale com um especialista'
              wrapper.querySelectorAll('input:not([type="hidden"]), select, textarea').forEach(function(el) {
                el.value = ''
              })
              if (abraThankYouUrl) {
                window.location.href = abraThankYouUrl
              } else {
                var abraModal = document.createElement('div')
                abraModal.style.cssText = 'position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(0,0,0,0.55);z-index:9999;display:flex;align-items:center;justify-content:center;'
                var abraModalBox = document.createElement('div')
                abraModalBox.style.cssText = 'background:#fff;border-radius:14px;padding:40px 48px;max-width:420px;width:90%;text-align:center;box-shadow:0 8px 48px rgba(0,0,0,0.22);'
                abraModalBox.innerHTML = '<p style="font-size:48px;margin:0 0 16px;line-height:1;">✅</p><h2 style="font-size:22px;font-weight:700;color:#1a1a1a;margin:0 0 10px;">Obrigado!</h2><p style="font-size:15px;color:#555;line-height:1.6;margin:0 0 28px;">Recebemos seu contato.<br>Em breve nossa equipe entrará em contato com você.</p>'
                var abraModalBtn = document.createElement('button')
                abraModalBtn.textContent = 'Fechar'
                abraModalBtn.style.cssText = 'padding:12px 36px;background:#111;color:#fff;border:none;border-radius:4px;font-size:13px;font-weight:700;cursor:pointer;letter-spacing:1.5px;text-transform:uppercase;transition:background 0.2s;'
                abraModalBtn.onmouseover = function() { abraModalBtn.style.background = '#333' }
                abraModalBtn.onmouseout = function() { abraModalBtn.style.background = '#111' }
                abraModalBtn.addEventListener('click', function() { document.body.removeChild(abraModal) })
                abraModal.addEventListener('click', function(e) { if (e.target === abraModal) document.body.removeChild(abraModal) })
                abraModalBox.appendChild(abraModalBtn)
                abraModal.appendChild(abraModalBox)
                document.body.appendChild(abraModal)
              }
            })
            .catch(function() {
              btn.disabled = false
              btn.textContent = 'Fale com um especialista'
            })
        })
      }

      // Only this snippet's own forms: the id pairs this JS with the HTML generated with it
      function abraInitPending() {
        document.querySelectorAll('.abra-form-wrapper[data-abra-snippet="${snippetId}"]').forEach(function(wrapper) {
          // Once per element, even with this JS pasted twice; a property (not an attribute) so a cloned form still sets up
          if (wrapper.abraSnippetReady || !wrapper.querySelector('.abra-form__submit')) return
          wrapper.abraSnippetReady = true
          abraInitForm(wrapper)
        })
      }
      abraInitPending()

      // Forms that show up later (e.g. modal content created on open) are set up as they appear
      function abraWatchForms() {
        abraInitPending()
        if (window.MutationObserver) {
          new MutationObserver(abraInitPending).observe(document.body, { childList: true, subtree: true })
        }
      }
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', abraWatchForms)
      } else {
        abraWatchForms()
      }
    })()
    </script>`

  return { html: htmlSnippet, js: jsSnippet }
}

/**
 * Component to preview the contact snippet.
 */
function ContactSnippetPreview({ snippet }: { snippet: { html: string; js: string } }) {
  const previewSrcDoc = useMemo(
    () =>
      `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:16px;background:#f0f0f0;">${snippet.html}${snippet.js}</body></html>`,
    [snippet]
  )

  return (
    <SectionCard
      variant="flush"
      title="Preview"
      actions={
        <Badge colorScheme="blue" variant="subtle">
          ao vivo
        </Badge>
      }
    >
      <Box bg="gray.50" p={4}>
        <iframe
          srcDoc={previewSrcDoc}
          style={{ width: '100%', minHeight: '460px', border: 'none', borderRadius: '8px' }}
          title="Preview do formulário gerado"
          sandbox="allow-scripts allow-same-origin"
        />
      </Box>
    </SectionCard>
  )
}

/**
 * Component to display the generated code with copy functionality.
 */
function ContactSnippetCode({ title, snippet, isDisabled }: { title: string; snippet: string; isDisabled?: boolean }) {
  return (
    <SectionCard
      variant="flush"
      title={title}
      actions={
        <CopyButton
          value={snippet}
          entity="código"
          label={isDisabled ? 'Complete a configuração para copiar' : undefined}
          showLabel
          minW="100px"
          mr={0}
          isDisabled={isDisabled}
        />
      }
    >
      <Box bg="gray.900" overflowX="auto">
        <Textarea
          value={snippet}
          readOnly
          rows={28}
          fontFamily="mono"
          fontSize="11px"
          color="green.300"
          bg="transparent"
          border="none"
          resize="vertical"
          _focus={{ outline: 'none', boxShadow: 'none' }}
          onClick={(e) => (e.target as HTMLTextAreaElement).select()}
          whiteSpace="pre"
          overflowWrap="normal"
          overflow="auto"
        />
      </Box>
    </SectionCard>
  )
}

export default function ContactSnippetPage() {
  const apiBaseUrl = process.env.REACT_APP_API_URL ?? '' // Url to form GET options on selects
  const [config, setConfig] = useState<ISnippetConfig>({
    courseFormatMode: 'preset',
    courseFormatPreset: 'Presencial',
    knowledgeAreaMode: 'preset',
    knowledgeAreaPreset: '',
    courseMode: 'preset',
    coursePreset: null,
    courseOptions: [],
    apiBaseUrl,
    thankYouUrl: '',
    formTitle: ''
  })
  const toast = useAppToast()
  const snippet = useMemo(() => generateSnippet(config, createSnippetId()), [config])
  const isPassportPreset = config.courseFormatMode === 'preset' && config.courseFormatPreset === PASSPORT_COURSE_FORMAT
  const isConfigValid = useMemo(() => {
    if (isPassportPreset) return true
    if (config.knowledgeAreaMode === 'preset' && !config.knowledgeAreaPreset) return false
    if (config.courseMode === 'preset' && !config.coursePreset) return false
    if (config.courseMode === 'user_choice' && config.courseOptions.length === 0) return false
    return true
  }, [
    isPassportPreset,
    config.knowledgeAreaMode,
    config.knowledgeAreaPreset,
    config.courseMode,
    config.coursePreset,
    config.courseOptions
  ])
  const courseService = useMemo(() => new CoursesService(), [])
  const [knowledgeAreas, setKnowledgeAreas] = useState<IKnowledgeArea[]>([])
  const [courses, setCourses] = useState<ISelectCourse[]>([])

  // Filter courses and knowledge areas based on the preset format so the config UI only shows valid options
  const formatFilteredCourses = useMemo(() => {
    if (config.courseFormatMode !== 'preset') return courses
    if (config.courseFormatPreset === 'Presencial') return courses.filter((c) => !!c.in_person)
    if (config.courseFormatPreset === 'Online Ao Vivo') return courses.filter((c) => !!c.live_online)
    if (config.courseFormatPreset === 'Online') return courses.filter((c) => !!c.recorded)
    return courses
  }, [courses, config.courseFormatMode, config.courseFormatPreset])

  const formatFilteredKnowledgeAreas = useMemo(() => {
    if (config.courseFormatMode !== 'preset') return knowledgeAreas
    return knowledgeAreas.filter((area) =>
      formatFilteredCourses.some((c) => c.knowledge_areas?.some((ka) => ka.name === area.name))
    )
  }, [knowledgeAreas, formatFilteredCourses, config.courseFormatMode])

  useEffect(() => {
    const fetchKnowledgeAreas = async () => {
      try {
        const areas = await courseService.getKnowledgeAreas()
        setKnowledgeAreas(areas)
      } catch (error) {
        setKnowledgeAreas([])
        toast.error('Erro ao carregar áreas de conhecimento', error)
      }
    }

    const fetchCourses = async () => {
      try {
        const courses = await courseService.listForSelect(true, true, true)
        setCourses(courses)
      } catch (error) {
        setCourses([])
        toast.error('Erro ao carregar cursos', error)
      }
    }

    fetchKnowledgeAreas()
    fetchCourses()
  }, [])

  /**
   * Helper to generate the course label with modality info for user choice options
   *
   * Ex: "Curso de Marketing (Presencial e Online Ao Vivo)"
   */
  const getCourseModalityLabel = (course: ISelectCourse) => {
    const modalities: string[] = []
    if (course.in_person) modalities.push('Presencial')
    if (course.live_online) modalities.push('Online Ao Vivo')
    if (course.recorded) modalities.push('Online Gravado')
    if (modalities.length === 0) return course.name
    if (modalities.length === 1) return `${course.name} (${modalities[0]})`
    const last = modalities.pop()!
    return `${course.name} (${modalities.join(', ')} e ${last})`
  }

  return (
    <>
      <PageTitle title="Gerador de Snippet de Contato" description="Monte blocos de contato para usar no site" />

      <Grid templateColumns={{ base: '1fr', xl: '380px 1fr' }} gap={6} alignItems="start">
        {/* Form configuration */}
        <GridItem>
          <SectionCard title="Configuração do Formulário">
            <VStack spacing={5} align="stretch">
              {/* Título do Formulário */}
              <Box borderTop="1px solid" borderColor="gray.100" pt={4}>
                <FormField label="Título do Formulário" mb={1}>
                  <Input
                    size="sm"
                    placeholder="Ex: Fale com um especialista"
                    value={config.formTitle}
                    onChange={(e) => setConfig((p) => ({ ...p, formTitle: e.target.value }))}
                  />
                </FormField>
                <Text variant="caption" mt={1}>
                  Se preenchido, será exibido acima do formulário.
                </Text>
              </Box>

              {/* Permanent Fields badges */}
              <Box>
                <FormField label="Campos Fixos" mb={1}>
                  <HStack spacing={2} flexWrap="wrap">
                    {['Nome', 'E-mail', 'Telefone'].map((permanentField) => (
                      <Badge key={permanentField} colorScheme="green" variant="subtle" py={1}>
                        {permanentField}
                      </Badge>
                    ))}
                  </HStack>
                </FormField>
                <Text variant="caption" mt={1}>
                  Sempre presentes no formulário gerado.
                </Text>
              </Box>

              {/* Course format options */}
              <Box borderTop="1px solid" borderColor="gray.100" pt={4}>
                <FormField label="Formato do Curso" mb={2}>
                  <RadioGroup
                    value={config.courseFormatMode}
                    onChange={(value) =>
                      setConfig((p) => ({ ...p, courseFormatMode: value as 'preset' | 'user_choice' }))
                    }
                  >
                    <Stack direction="column" spacing={2}>
                      <Radio value="preset">Pré-selecionar</Radio>

                      <Radio value="user_choice">Deixar o usuário escolher</Radio>
                    </Stack>
                  </RadioGroup>
                </FormField>

                {/* Course format preset options */}
                {config.courseFormatMode === 'preset' && (
                  <FormSelect
                    mt={2}
                    size="sm"
                    value={config.courseFormatPreset}
                    onChange={(value) => setConfig((p) => ({ ...p, courseFormatPreset: value }))}
                    options={PRESET_FORMAT_OPTIONS.map((courseFormatOption) => ({
                      value: courseFormatOption.value,
                      label: courseFormatOption.label
                    }))}
                    hideEmptyOption
                  />
                )}

                {/* Passport preset disclaimer */}
                {isPassportPreset && (
                  <Text variant="caption" mt={1}>
                    O Passaporte não pede área nem curso: o formulário terá só os campos fixos.
                  </Text>
                )}

                {/* Course format user choice disclaimer */}
                {config.courseFormatMode === 'user_choice' && (
                  <Text variant="caption" mt={1}>
                    Será gerado um <code>{'<select>'}</code> com as {COURSE_FORMAT_OPTIONS.length} opções. O Passaporte
                    só está disponível em Pré-selecionar.
                  </Text>
                )}
              </Box>

              {/* Knowledge area options (hidden, not cleared, for a preset passport) */}
              <Box borderTop="1px solid" borderColor="gray.100" pt={4} hidden={isPassportPreset}>
                <FormField label="Área do Curso" mb={2}>
                  <RadioGroup
                    value={config.knowledgeAreaMode}
                    onChange={(value) => {
                      const newMode = value as 'preset' | 'user_choice'
                      setConfig((p) => ({
                        ...p,
                        knowledgeAreaMode: newMode,
                        knowledgeAreaPreset: '',
                        coursePreset: null,
                        courseOptions: [],
                        courseMode:
                          newMode === 'user_choice' && p.courseMode === 'preset' ? 'user_choice' : p.courseMode
                      }))
                    }}
                  >
                    <Stack direction="column" spacing={2}>
                      <Radio value="preset">Pré-selecionar</Radio>

                      <Radio value="user_choice">Deixar o usuário escolher</Radio>
                    </Stack>
                  </RadioGroup>
                </FormField>

                {/* Knowledge area preset options */}
                {config.knowledgeAreaMode === 'preset' && (
                  <FormSelect
                    mt={2}
                    size="sm"
                    value={config.knowledgeAreaPreset}
                    onChange={(value) => {
                      setConfig((p) => ({
                        ...p,
                        knowledgeAreaPreset: value,
                        coursePreset: null,
                        courseOptions: []
                      }))
                    }}
                    options={formatFilteredKnowledgeAreas.map((knowledgeArea) => ({
                      value: knowledgeArea.name,
                      label: knowledgeArea.name
                    }))}
                    placeholder="Selecione uma área"
                  />
                )}

                {/* Knowledge area user choice disclaimer */}
                {config.knowledgeAreaMode === 'user_choice' && (
                  <Text variant="caption" mt={1}>
                    O formulário exibirá as áreas disponíveis para o usuário escolher.
                  </Text>
                )}
              </Box>

              {/* Curso */}
              <Box borderTop="1px solid" borderColor="gray.100" pt={4} hidden={isPassportPreset}>
                <FormField label="Curso" mb={2}>
                  <RadioGroup
                    value={config.courseMode}
                    onChange={(value) =>
                      setConfig((p) => ({ ...p, courseMode: value as 'preset' | 'user_choice' | 'all' }))
                    }
                  >
                    <Stack direction="column" spacing={2}>
                      <Radio value="preset" isDisabled={config.knowledgeAreaMode === 'user_choice'}>
                        Pré-selecionar
                      </Radio>

                      <Radio value="user_choice">Deixar o usuário escolher</Radio>

                      <Radio value="all">Disponibilizar todos</Radio>
                    </Stack>
                  </RadioGroup>
                </FormField>

                {/* All courses disclaimer */}
                {config.courseMode === 'all' && (
                  <Text variant="caption" mt={1}>
                    O formulário buscará todos os cursos disponíveis via API e os exibirá no select.
                  </Text>
                )}

                {/* Course preset options */}
                {config.courseMode === 'preset' && (
                  <FormSelect
                    mt={2}
                    size="sm"
                    value={config.coursePreset?.id?.toString() ?? ''}
                    onChange={(value) => {
                      const found = courses.find((c) => c.id.toString() === value) ?? null
                      setConfig((p) => ({ ...p, coursePreset: found }))
                    }}
                    options={formatFilteredCourses
                      .filter(
                        config.knowledgeAreaPreset
                          ? (course) => course.knowledge_areas?.some((ka) => ka.name === config.knowledgeAreaPreset)
                          : () => true
                      )
                      .map((course) => ({ value: course.id.toString(), label: getCourseModalityLabel(course) }))}
                    placeholder="Selecione um curso"
                  />
                )}

                {/* Course user choice options */}
                {config.courseMode === 'user_choice' && (
                  <Box mt={2}>
                    <SearchableSelect
                      isMulti
                      value={config.courseOptions.map((c) => c.id.toString())}
                      options={formatFilteredCourses
                        .filter(
                          config.knowledgeAreaPreset
                            ? (course) => course.knowledge_areas?.some((ka) => ka.name === config.knowledgeAreaPreset)
                            : () => true
                        )
                        .map((course) => ({
                          value: course.id.toString(),
                          label: getCourseModalityLabel(course)
                        }))}
                      onChange={(courseIds) => {
                        const selected = courseIds
                          .map((id) => courses.find((c) => c.id.toString() === id))
                          .filter((c): c is ISelectCourse => !!c)
                        setConfig((p) => ({ ...p, courseOptions: selected }))
                      }}
                      placeholder="Selecione um ou mais cursos"
                      entity="curso"
                    />

                    <Text variant="caption" mb={2}>
                      Selecione os cursos que aparecerão no select.
                    </Text>

                    {config.courseOptions.length === 0 && (
                      <Text fontSize="xs" color="orange.500" mt={1}>
                        Nenhuma opção selecionada — o campo de curso não será gerado.
                      </Text>
                    )}
                    {config.courseFormatMode === 'user_choice' &&
                      config.courseOptions.length > 0 &&
                      !config.courseOptions.some((c) => c.in_person) && (
                        <Text fontSize="xs" color="orange.500" mt={1}>
                          Nenhum curso <strong>Presencial</strong> selecionado — usuários que escolherem esta modalidade
                          não verão cursos disponíveis.
                        </Text>
                      )}
                    {config.courseFormatMode === 'user_choice' &&
                      config.courseOptions.length > 0 &&
                      !config.courseOptions.some((c) => c.live_online) && (
                        <Text fontSize="xs" color="orange.500" mt={1}>
                          Nenhum curso <strong>Online Ao Vivo</strong> selecionado — usuários que escolherem esta
                          modalidade não verão cursos disponíveis.
                        </Text>
                      )}
                    {config.courseFormatMode === 'user_choice' &&
                      config.courseOptions.length > 0 &&
                      !config.courseOptions.some((c) => c.recorded) && (
                        <Text fontSize="xs" color="orange.500" mt={1}>
                          Nenhum curso <strong>Online Gravado</strong> selecionado — usuários que escolherem esta
                          modalidade não verão cursos disponíveis.
                        </Text>
                      )}
                  </Box>
                )}
              </Box>

              {/* Link de Agradecimento */}
              <Box borderTop="1px solid" borderColor="gray.100" pt={4}>
                <FormField label="Link de Agradecimento" mb={1}>
                  <Input
                    size="sm"
                    placeholder="https://..."
                    value={config.thankYouUrl}
                    onChange={(e) => setConfig((p) => ({ ...p, thankYouUrl: e.target.value }))}
                  />
                </FormField>
                <Text variant="caption" mt={1}>
                  Ao enviar, o usuário será redirecionado para este link. Se vazio, um modal de agradecimento será
                  exibido.
                </Text>
              </Box>
            </VStack>
          </SectionCard>
        </GridItem>

        <GridItem>
          <VStack spacing={6} align="stretch">
            {/* Form preview */}
            <ContactSnippetPreview snippet={snippet} />

            {!isConfigValid && (
              <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" px={4} py={3}>
                <Text fontWeight="bold" fontSize="sm" color="orange.700" mb={1}>
                  Configuração incompleta
                </Text>
                <Text fontSize="xs" color="orange.600">
                  Preencha todos os campos obrigatórios no painel de configuração para gerar e copiar o código.
                </Text>
              </Box>
            )}

            <Box>
              <Flex gap={6} flexDirection={{ base: 'column', lg: 'row' }}>
                {/* HTML Code */}
                <Box w={'full'}>
                  <ContactSnippetCode title="Código HTML" snippet={snippet.html} isDisabled={!isConfigValid} />
                </Box>

                {/* JavaScript Code */}
                <Box w={'full'}>
                  <ContactSnippetCode title="Código JavaScript" snippet={snippet.js} isDisabled={!isConfigValid} />
                </Box>
              </Flex>

              {/* HTML/JS pairing notes */}
              <Text variant="caption" mt={2}>
                O HTML e o JavaScript formam um par: copie os dois juntos, sem mudar a configuração entre um e outro.
                Trocar só um deles num formulário já publicado faz o envio parar sem aviso: a página recarrega e o lead
                se perde, mesmo que os formulários sejam idênticos.
              </Text>
              <Text variant="caption">
                O JavaScript de um snippet antigo (HTML sem <code>data-abra-snippet</code>) também prepara o formulário
                novo da mesma página, que passa a enviar duas vezes: gere o antigo de novo.
              </Text>
            </Box>
          </VStack>
        </GridItem>
      </Grid>
    </>
  )
}
